/**
 * Wishlist Manager for Kavora Fashion
 * localStorage-based wishlist system
 */

class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.productHandle = this.getAttribute('product-handle');
    this.productData = {
      handle: this.productHandle,
      title: this.getAttribute('product-title'),
      price: this.getAttribute('product-price'),
      image: this.getAttribute('product-image'),
      url: this.getAttribute('product-url')
    };
  }

  connectedCallback() {
    this.render();
    this.updateState();
    this.addEventListener('click', this.toggle.bind(this));
  }

  render() {
    this.innerHTML = `
      <button type="button" class="wishlist-button" aria-label="Add to wishlist">
        <svg class="wishlist-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 18.35l-1.45-1.32C3.4 12.36 0 9.28 0 5.5 0 2.42 2.42 0 5.5 0 7.24 0 8.91.81 10 2.09 11.09.81 12.76 0 14.5 0 17.58 0 20 2.42 20 5.5c0 3.78-3.4 6.86-8.55 11.54L10 18.35z" fill="currentColor"/>
        </svg>
      </button>
    `;
  }

  updateState() {
    const wishlist = this.getWishlist();
    const isInWishlist = wishlist.some(item => item.handle === this.productHandle);
    this.classList.toggle('is-active', isInWishlist);
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const wishlist = this.getWishlist();
    const index = wishlist.findIndex(item => item.handle === this.productHandle);
    
    if (index > -1) {
      wishlist.splice(index, 1);
      this.classList.remove('is-active');
    } else {
      wishlist.push(this.productData);
      this.classList.add('is-active');
    }
    
    this.saveWishlist(wishlist);
    this.dispatchEvent(new CustomEvent('wishlist:updated', {
      bubbles: true,
      detail: { wishlist, action: index > -1 ? 'removed' : 'added' }
    }));
  }

  getWishlist() {
    try {
      return JSON.parse(localStorage.getItem('kavora_wishlist') || '[]');
    } catch (e) {
      return [];
    }
  }

  saveWishlist(wishlist) {
    try {
      localStorage.setItem('kavora_wishlist', JSON.stringify(wishlist));
    } catch (e) {
      console.error('Failed to save wishlist:', e);
    }
  }
}

class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    this.render();
    this.bindEvents();
    this.updateContent();
    
    // Listen for wishlist updates
    document.addEventListener('wishlist:updated', () => this.updateContent());
  }

  render() {
    this.innerHTML = `
      <div class="wishlist-drawer" role="dialog" aria-label="Wishlist">
        <div class="wishlist-drawer__overlay"></div>
        <div class="wishlist-drawer__content">
          <header class="wishlist-drawer__header">
            <h2>My Wishlist</h2>
            <button type="button" class="wishlist-drawer__close" aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 20 20">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </header>
          <div class="wishlist-drawer__body"></div>
        </div>
      </div>
    `;
  }

  bindEvents() {
    this.querySelector('.wishlist-drawer__close').addEventListener('click', () => this.close());
    this.querySelector('.wishlist-drawer__overlay').addEventListener('click', () => this.close());
  }

  open() {
    this.isOpen = true;
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  updateContent() {
    const wishlist = this.getWishlist();
    const body = this.querySelector('.wishlist-drawer__body');
    
    if (wishlist.length === 0) {
      body.innerHTML = `
        <div class="wishlist-empty">
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <path d="M30 55l-4.35-3.96C10.2 37.08 0 27.84 0 16.5 0 7.26 7.26 0 16.5 0c5.22 0 10.23 2.43 13.5 6.27C32.77 2.43 37.78 0 43.5 0 52.74 0 60 7.26 60 16.5c0 11.34-10.2 20.58-25.65 34.54L30 55z" fill="currentColor" opacity="0.1"/>
          </svg>
          <p>Your wishlist is empty</p>
          <a href="/collections/all" class="button">Continue Shopping</a>
        </div>
      `;
    } else {
      body.innerHTML = `
        <div class="wishlist-items">
          ${wishlist.map(item => `
            <div class="wishlist-item" data-handle="${item.handle}">
              <a href="${item.url}" class="wishlist-item__image">
                <img src="${item.image}" alt="${item.title}" loading="lazy">
              </a>
              <div class="wishlist-item__details">
                <a href="${item.url}" class="wishlist-item__title">${item.title}</a>
                <div class="wishlist-item__price">${item.price}</div>
              </div>
              <button type="button" class="wishlist-item__remove" data-handle="${item.handle}" aria-label="Remove from wishlist">
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
            </div>
          `).join('')}
        </div>
      `;
      
      // Bind remove buttons
      body.querySelectorAll('.wishlist-item__remove').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const handle = e.currentTarget.getAttribute('data-handle');
          this.removeItem(handle);
        });
      });
    }
  }

  removeItem(handle) {
    const wishlist = this.getWishlist();
    const filtered = wishlist.filter(item => item.handle !== handle);
    this.saveWishlist(filtered);
    this.updateContent();
    
    // Update all wishlist buttons
    document.querySelectorAll(`wishlist-button[product-handle="${handle}"]`).forEach(btn => {
      btn.updateState();
    });
  }

  getWishlist() {
    try {
      return JSON.parse(localStorage.getItem('kavora_wishlist') || '[]');
    } catch (e) {
      return [];
    }
  }

  saveWishlist(wishlist) {
    try {
      localStorage.setItem('kavora_wishlist', JSON.stringify(wishlist));
      document.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { wishlist }
      }));
    } catch (e) {
      console.error('Failed to save wishlist:', e);
    }
  }
}

// Register custom elements
if (!window.customElements.get('wishlist-button')) {
  window.customElements.define('wishlist-button', WishlistButton);
}

if (!window.customElements.get('wishlist-drawer')) {
  window.customElements.define('wishlist-drawer', WishlistDrawer);
}

// Global wishlist toggle function
window.toggleWishlistDrawer = function() {
  const drawer = document.querySelector('wishlist-drawer');
  if (drawer) {
    if (drawer.isOpen) {
      drawer.close();
    } else {
      drawer.open();
    }
  }
};

// Update header wishlist counter
function updateWishlistCounter() {
  try {
    const wishlist = JSON.parse(localStorage.getItem('kavora_wishlist') || '[]');
    const counter = document.getElementById('wishlist-count');
    if (counter) {
      if (wishlist.length > 0) {
        counter.textContent = wishlist.length;
        counter.style.display = 'flex';
      } else {
        counter.style.display = 'none';
      }
    }
  } catch (e) {
    console.error('Failed to update wishlist counter:', e);
  }
}

document.addEventListener('DOMContentLoaded', updateWishlistCounter);

// Update counter when wishlist changes
document.addEventListener('wishlist:updated', updateWishlistCounter);
